
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Maximize, Minimize } from "lucide-react";

const slides = [
  {
    title: "Solving FOMO 2.0",
    image: "/lovable-uploads/aa035d9c-d06a-477c-b976-3e636bdb9090.png",
    alt: "Solving FOMO 2.0"
  },
  {
    title: "FOMO 2.0",
    image: "/lovable-uploads/48e57e7d-cb4f-4afa-900c-34a7524ab1f6.png",
    alt: "FOMO 2.0"
  },
  {
    title: "Data Entropy in Energy",
    image: "/lovable-uploads/0b3da2bf-f338-461d-b26f-a968f1b5ffbc.png",
    alt: "Data Entropy in Energy"
  },
  {
    title: "Knowledge Entropy in Construction",
    image: "/lovable-uploads/951e1ab2-2575-4c48-8196-a62d4d7b7ef4.png",
    alt: "Knowledge Entropy in Construction"
  },
  {
    title: "The Shrinking Workforce",
    image: "/lovable-uploads/267e4f9b-a0e3-49bf-b060-a13e1fd3ec58.png",
    alt: "The Shrinking Workforce"
  },
  {
    title: "Will We Rise to the Challenge",
    image: "/lovable-uploads/9ff03278-3d6d-4ddb-9537-41d37a2f5bd1.png",
    alt: "Will We Rise to the Challenge"
  },
  {
    title: "Our Mission",
    image: "/lovable-uploads/5c1d9eac-fe12-442a-952c-34e17f199554.png",
    alt: "Our Mission"
  },
  {
    title: "Thank You",
    image: "/lovable-uploads/d1e84793-5491-42fa-8e83-c1d50881821c.png",
    alt: "Thank You"
  },
  {
    title: "Panel Discussion 1",
    image: "/lovable-uploads/49a9f900-ae43-41f2-b422-5e8739798b46.png",
    alt: "Panel Discussion 1"
  },
  {
    title: "Panel Discussion 2",
    image: "/lovable-uploads/14ccb3b3-9789-4a35-8975-38a785536c18.png",
    alt: "Panel Discussion 2"
  },
  {
    title: "Panel Discussion 3",
    image: "/lovable-uploads/08717a3c-2908-48e5-a4cf-34069b1cfa45.png",
    alt: "Panel Discussion 3"
  },
  {
    title: "Integrated Special Purpose AI",
    image: "/lovable-uploads/98ebc55e-465f-4fc8-94b8-3e49e1e193bf.png",
    alt: "Integrated Special Purpose AI - AI in Oil & Gas"
  }
];

const AiOilGasPresentation = () => {
  const navigate = useNavigate();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [current, setCurrent] = useState(0);
  const presRef = useRef<HTMLDivElement | null>(null);

  const handleFullscreenChange = () => {
    const el = document.fullscreenElement || 
      // @ts-ignore
      document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
    setIsFullscreen(!!el);
  };

  const enterFullscreen = () => {
    if (presRef.current) {
      if (presRef.current.requestFullscreen) presRef.current.requestFullscreen();
      // @ts-ignore
      else if (presRef.current.webkitRequestFullscreen) presRef.current.webkitRequestFullscreen();
      // @ts-ignore
      else if (presRef.current.mozRequestFullScreen) presRef.current.mozRequestFullScreen();
      // @ts-ignore
      else if (presRef.current.msRequestFullscreen) presRef.current.msRequestFullscreen();
    }
  };

  const exitFullscreen = () => {
    if (document.exitFullscreen) document.exitFullscreen();
    // @ts-ignore
    else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    // @ts-ignore
    else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
    // @ts-ignore
    else if (document.msExitFullscreen) document.msExitFullscreen();
  };

  const toggleFullscreen = () => {
    isFullscreen ? exitFullscreen() : enterFullscreen();
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const handleSelect = (api: any) => {
    setCurrent(api.selectedScrollSnap());
  };

  return (
    <div
      ref={presRef}
      className={`min-h-screen bg-gradient-to-b from-primary/5 to-background p-2 md:p-8 transition-all duration-300 ${isFullscreen ? 'fixed inset-0 z-[9999] bg-background p-0' : ''}`}
    >
      <div className="max-w-6xl mx-auto">
        {!isFullscreen && current === slides.length - 1 && (
          <div className="mb-8">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="mb-8"
            >
              Exit Presentation
            </Button>
          </div>
        )}

        <Carousel className="w-full" setApi={api => api && api.on('select', () => handleSelect(api))}>
          <CarouselContent>
            {slides.map((slide, idx) => (
              <CarouselItem key={slide.title}>
                <Card className="relative p-2 md:p-12 min-h-[400px] md:min-h-[600px] flex items-center justify-center overflow-hidden">
                  <img
                    src={slide.image}
                    alt={slide.alt}
                    className="w-full h-auto object-contain max-w-5xl max-h-[70vh] mx-auto transition-all duration-300"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleFullscreen}
                    aria-label={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                    className="absolute top-2 right-2 z-10 bg-white/80 hover:bg-white"
                    tooltip={isFullscreen ? "Exit Fullscreen" : "Go Fullscreen"}
                  >
                    {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                  </Button>
                  {idx === slides.length - 1 && isFullscreen && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        exitFullscreen();
                        setTimeout(() => {
                          navigate('/');
                        }, 350);
                      }}
                      className="absolute bottom-6 left-1/2 -translate-x-1/2"
                    >
                      Exit Presentation
                    </Button>
                  )}
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex justify-center gap-4 mt-8">
            <CarouselPrevious />
            <CarouselNext />
          </div>
        </Carousel>
      </div>
    </div>
  );
};

export default AiOilGasPresentation;
